import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Container, Card, Row, Col, Table, Image, Badge, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { getCastaways } from '../api/castaways';
import { Castaway, Draft, Member } from '../types';
import '../App.css';

interface EpisodeScore {
  tribeImmunityWins: number;
  tribeRewardWins: number;
  individualRewardWins: number;
  rightSideOfVote: number;
  individualImmunityWins: number;
  votesAgainst: number;
  idolObtained: number;
  advantageObtained: number;
  playedIdolAdvantage: number;
  advantageAbandoned: number;
  idolAbandoned: number;
  shotInDarkWin: number;
  coinFlipWin?: number;
  finalPlacement: number;
  votedOut: boolean;
  involuntaryEvacuation?: boolean;
}

interface AllScores {
  [episode: string]: {
    [castawayId: string]: EpisodeScore;
  };
}

const SeasonHighlights: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const targetUserName = queryParams.get('userName');

  const [userInfo, setUserInfo] = useState<any>(null);
  const [league, setLeague] = useState<any>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [allScores, setAllScores] = useState<AllScores>({});
  const [castaways, setCastaways] = useState<Castaway[]>([]);
  const [eliminationPicks, setEliminationPicks] = useState<{ [episode: string]: number }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(0);
  const [rank, setRank] = useState<number>(0);

  const fetchData = useCallback(async () => {
    if (!leagueId) return;
    setIsLoading(true);
    setError(null);
    try {
      // 1. Fetch League & Season Context
      const leaguesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      const leaguesData = await leaguesRes.json();
      const currentLeague = leaguesData.leagues.find((l: any) => l.id === leagueId);
      if (!currentLeague) throw new Error('League not found');
      
      const season = currentLeague.season || 49;
      setSelectedSeason(season);
      setLeague(currentLeague);

      // 2. Load User for this specific season
      const userKey = `fantasyUser_${season}`;
      let storedUser = localStorage.getItem(userKey);
      let user = storedUser ? JSON.parse(storedUser) : null;

      // If a specific userName was requested, prioritize that
      if (targetUserName) {
          const matchingMember = currentLeague.members?.find((m: any) => m.userName === targetUserName);
          if (matchingMember) {
              user = {
                  userName: matchingMember.userName,
                  survivorName: matchingMember.survivorName,
                  realName: matchingMember.realName,
                  leagueId: leagueId,
                  email: matchingMember.email
              };
          }
      }

      // Fallback: If no season-specific user, check if we have any logged-in user with an email
      if (!user) {
        const primaryUserStr = localStorage.getItem('fantasyUser');
        if (primaryUserStr) {
            const primaryUser = JSON.parse(primaryUserStr);
            if (primaryUser.email) {
                // Try to find this user's entry for THIS league specifically
                const userRes = await fetch(`${process.env.REACT_APP_API_URL}/api/users/by-email/${primaryUser.email}`);
                const userData = await userRes.json();
                
                // If multiple entries for same email, and no targetUserName, this might be ambiguous.
                // We'll pick the first one matching this leagueId.
                const matchingEntry = userData.users?.find((u: any) => u.leagueId === leagueId);
                if (matchingEntry) {
                    user = {
                        userName: matchingEntry.userName,
                        survivorName: matchingEntry.survivorName,
                        realName: matchingEntry.realName,
                        leagueId: matchingEntry.leagueId,
                        email: primaryUser.email
                    };
                }
            }
        }
      }

      if (!user) throw new Error('User entry not found for this league/season. Please log in on the home page.');
      setUserInfo(user);

      // 3. Fetch specific data
      const [scoresRes, draftsRes, picksRes, castawaysRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/scores/${season}`),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/drafts`),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/elimination-picks`),
        getCastaways(leagueId)
      ]);

      const scoresData = await scoresRes.json();
      const draftsData = await draftsRes.json();
      const picksData = await picksRes.json();

      const scores = scoresData.scores || {};
      const drafts = draftsData.drafts || {};
      const eliminationPicks = picksData.eliminationPicks || {};

      setAllScores(scores);
      setDraft(drafts[user.userName] || null);
      setEliminationPicks(eliminationPicks[user.userName] || {});
      setCastaways(castawaysRes);

      // 4. Calculate Rank
      const getScore = (castawayId: number, epScores: any) => {
          if (!epScores) return 0;
          const s = epScores[castawayId];
          if (!s) return 0;
          let pts = 0;
          pts += (s.tribeImmunityWins || 0) * 1;
          pts += (s.tribeRewardWins || 0) * 1;
          pts += (s.individualRewardWins || 0) * 2;
          pts += (s.rightSideOfVote || 0) * 1;
          pts += (s.individualImmunityWins || 0);
          pts += (s.votesAgainst || 0) * -0.5;
          pts += (s.idolObtained || 0) * 4;
          pts += (s.advantageObtained || 0) * 2;
          pts += (s.playedIdolAdvantage || 0) * 2;
          pts += (s.advantageAbandoned || 0) * -2;
          pts += (s.idolAbandoned || 0) * -3;
          pts += (s.shotInDarkWin || 0) * 4;
          pts += (s.coinFlipWin || 0) * 4;
          if (s.finalPlacement === 1) pts += 25;
          if (s.finalPlacement === 2) pts += 10;
          if (s.finalPlacement === 3) pts += 5;
          return pts;
      };

      const memberScores = currentLeague.members.map((m: any) => {
          const d = drafts[m.userName];
          let total = 0;
          if (d) {
              Object.entries(scores).forEach(([epKey, epScores]: [string, any]) => {
                  const epNum = parseInt(epKey.replace('episode', ''), 10);
                  if (epNum === 1) return; // Episode 1 is non-scoring tracking
                  d.team.forEach((cid: number) => {
                      total += getScore(cid, epScores);
                  });
              });
              const uPicks = eliminationPicks[m.userName];
              if (uPicks) {
                  Object.entries(uPicks).forEach(([ep, cid]) => {
                      const epNum = parseInt(ep.replace('episode', ''), 10);
                      if (epNum === 1) return;
                      const epS = scores[ep];
                      if (epS && Object.entries(epS).some(([vcid, s]: any) => Number(vcid) === cid && s.votedOut)) {
                          total += 3;
                      }
                  });
              }
          }
          return { userName: m.userName, score: total };
      }).sort((a: any, b: any) => b.score - a.score);

      const userRank = memberScores.findIndex((m: any) => m.userName === user.userName) + 1;
      setRank(userRank);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [leagueId, targetUserName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCastawayById = (id: number) => castaways.find(c => c.id === id);

  const calculatePoints = (castawayId: number, epScores: EpisodeScore) => {
    let pts = 0;
    pts += (epScores.tribeImmunityWins || 0) * 1;
    pts += (epScores.tribeRewardWins || 0) * 1;
    pts += (epScores.individualRewardWins || 0) * 2;
    pts += (epScores.rightSideOfVote || 0) * 1;
    pts += (epScores.individualImmunityWins || 0);
    pts += (epScores.votesAgainst || 0) * -0.5;
    pts += (epScores.idolObtained || 0) * 4;
    pts += (epScores.advantageObtained || 0) * 2;
    pts += (epScores.playedIdolAdvantage || 0) * 2;
    pts += (epScores.advantageAbandoned || 0) * -2;
    pts += (epScores.idolAbandoned || 0) * -3;
    pts += (epScores.shotInDarkWin || 0) * 4;
    pts += (epScores.coinFlipWin || 0) * 4;
    if (epScores.finalPlacement === 1) pts += 25;
    if (epScores.finalPlacement === 2) pts += 10;
    if (epScores.finalPlacement === 3) pts += 5;
    return pts;
  };

  if (isLoading) return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;
  if (error) return <Container className="mt-5"><Alert variant="danger">{error}</Alert><Link to="/" className="btn btn-secondary">Back to Home</Link></Container>;

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h3" className="card-title-custom text-center d-flex justify-content-between align-items-center flex-wrap">
            <span>Season {selectedSeason} Highlights: {userInfo?.userName}</span>
            {rank > 0 && <Badge bg="primary" pill>Place: {rank}</Badge>}
        </Card.Header>
        <Card.Body>
          <div className="text-center mb-4">
            <Link to="/" className="btn btn-secondary me-2">Back to Home</Link>
            <Link to={`/league/${leagueId}`} className="btn btn-primary">View Full Standings</Link>
          </div>
          <p className="text-center text-muted">League: {league?.name}</p>

          <Row className="mb-4">
            <Col md={12}>
              <h5>Drafted Team Highlights</h5>
              <div className="d-flex flex-wrap justify-content-center">
                {draft?.team.map(id => {
                  const c = getCastawayById(id);
                  if (!c) return null;
                  const isOut = Object.values(allScores).some(ep => ep[id]?.votedOut || ep[id]?.involuntaryEvacuation || (ep[id] as any)?.voluntaryExit);
                  return (
                    <Card key={id} className={`m-2 text-center ${isOut ? 'opacity-75 bg-light' : ''}`} style={{ width: '150px' }}>
                      <Card.Body className="p-2">
                        <Image src={c.imageUrl} roundedCircle width="80" height="80" className="mb-2" style={{ objectFit: 'cover' }} />
                        <div className="fw-bold">{c.name}</div>
                        {isOut && <Badge bg="danger">Out of Game</Badge>}
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <h5>Weekly Elimination Picks</h5>
              <ListGroup variant="flush">
                {Object.entries(eliminationPicks).sort((a, b) => Number(a[0]) - Number(b[0])).map(([ep, id]) => {
                  const c = getCastawayById(id);
                  const epScores = allScores[ep];
                  const wasCorrect = epScores && Object.entries(epScores).some(([cid, s]) => Number(cid) === id && s.votedOut);
                  return (
                    <ListGroup.Item key={ep} className="d-flex justify-content-between align-items-center">
                      <span>Episode {ep}: <strong>{c?.name || 'Unknown'}</strong></span>
                      <Badge bg={wasCorrect ? 'success' : 'secondary'}>{wasCorrect ? '+3 Pts' : 'No Pts'}</Badge>
                    </ListGroup.Item>
                  );
                })}
                {Object.keys(eliminationPicks).length === 0 && <ListGroup.Item className="text-muted">No picks made.</ListGroup.Item>}
              </ListGroup>
            </Col>
            <Col md={6}>
              <h5>Swap History</h5>
              <ListGroup variant="flush">
                {draft?.swapUsed && (
                    <ListGroup.Item>
                        <Badge bg="warning" text="dark">One-Time Swap Used</Badge>
                        <div className="small mt-1 text-muted">Effective Episode: {draft.swapEffectiveEpisode}</div>
                    </ListGroup.Item>
                )}
                {draft?.swapHistory?.map((s, idx) => (
                    <ListGroup.Item key={idx}>
                        <Badge bg="info">Medical Swap</Badge>
                        <div className="mt-1 small">
                            {getCastawayById(s.swappedOutCastawayId)?.name} &rarr; {getCastawayById(s.swappedInCastawayId)?.name}
                        </div>
                    </ListGroup.Item>
                ))}
                {!draft?.swapUsed && (!draft?.swapHistory || draft.swapHistory.length === 0) && (
                    <ListGroup.Item className="text-muted">No swaps used this season.</ListGroup.Item>
                )}
              </ListGroup>
            </Col>
          </Row>

          <Row>
            <Col>
              <h5>Season Timeline (Key Events)</h5>
              <Table striped bordered hover size="sm" responsive className="mt-3">
                <thead>
                  <tr>
                    <th>Episode</th>
                    <th>Your Players Scored</th>
                    <th>Voted Out</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(allScores).sort((a, b) => Number(a[0]) - Number(b[0])).map(([ep, epScores]) => {
                    const yourActivePlayers = draft?.team.filter(id => {
                        // This logic is simplified; real logic would check if they were on team AT that episode
                        return !!epScores[id];
                    }) || [];
                    
                    const votedOutThisEp = Object.entries(epScores).find(([_, s]) => s.votedOut)?.[0];
                    const votedOutName = votedOutThisEp ? getCastawayById(Number(votedOutThisEp))?.name : null;

                    return (
                      <tr key={ep}>
                        <td>Episode {ep}</td>
                        <td>
                          {yourActivePlayers.map(id => (
                            <div key={id} className="small">
                                {getCastawayById(id)?.name}: <strong>+{calculatePoints(id, epScores[id])}</strong>
                            </div>
                          ))}
                        </td>
                        <td>{votedOutName ? <Badge bg="danger">{votedOutName}</Badge> : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Col>
          </Row>

          <div className="text-center mt-4">
            <Link to="/" className="btn btn-secondary me-2">Back to Home</Link>
            <Link to={`/league/${leagueId}`} className="btn btn-primary">View Full Standings</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SeasonHighlights;